/* ===== Admin UI Functions ===== */
import { $ } from '../utils/helpers.js?v=83.79';
import { getCurrentSettings, setCurrentSettings } from '../services/settings.js?v=83.79';
import { clearPreview, appendPreview, renderPreview } from '../utils/ui.js?v=83.79';
import { renderPrizeOptions } from './prizes.js?v=83.79';
import { initAdminSections, updateSectionSummaries } from './admin-collapsible.js?v=83.79';
import { initClientLayoutPresetControls } from './client-layout-renderer.js?v=83.79';
import { logger } from '../utils/logger.js?v=83.79';
import { sanitizeImageSrc } from '../utils/url.js?v=83.79';
import { db } from '../config/firebase.js?v=83.79';
import { getCurrentUser } from '../services/auth.js?v=83.79';

let responsiblesUnsub = null;

function getAdvancedClaimsDocRef() {
  const currentUser = getCurrentUser();
  if (!currentUser) return null;
  return db.collection('advancedClaims').doc(currentUser.uid);
}

function getServerTimestampValue() {
  return firebase.firestore.FieldValue.serverTimestamp();
}

function setAdvancedClaimsMode(mode) {
  const standardBtn = $('claimsStandardBtn');
  const advancedBtn = $('claimsAdvancedBtn');
  const advancedSection = $('advancedResponsiblesSection');
  if (!standardBtn || !advancedBtn || !advancedSection) return;

  const isAdvanced = mode === 'advanced';
  standardBtn.classList.toggle('btn-primary', !isAdvanced);
  standardBtn.classList.toggle('btn-ghost', isAdvanced);
  advancedBtn.classList.toggle('btn-primary', isAdvanced);
  advancedBtn.classList.toggle('btn-ghost', !isAdvanced);
  advancedSection.classList.toggle('hidden', !isAdvanced);
}

async function saveAdvancedClaimsMode(mode) {
  const docRef = getAdvancedClaimsDocRef();
  if (!docRef) return;
  try {
    await docRef.set({
      mode,
      updatedAt: getServerTimestampValue()
    }, { merge: true });
  } catch (error) {
    logger.warn('[ADV CLAIMS] Failed to save mode:', error);
  }
}

async function loadAdvancedClaimsMode() {
  const section = $('advancedResponsiblesSection');
  const docRef = getAdvancedClaimsDocRef();
  if (!section || !docRef) {
    setAdvancedClaimsMode('standard');
    if (section) section.style.display = 'none';
    const receiptChkOff = $('requireReceiptNumberChk');
    if (receiptChkOff) receiptChkOff.checked = false;
    return;
  }

  try {
    const snap = await docRef.get();
    const mode = (snap.exists && snap.data()?.mode === 'advanced') ? 'advanced' : 'standard';
    setAdvancedClaimsMode(mode);
    section.style.display = mode === 'advanced' ? 'block' : 'none';
    const receiptChk = $('requireReceiptNumberChk');
    if (receiptChk) {
      receiptChk.checked = !!(snap.exists && snap.data()?.requireReceiptNumber === true);
    }
  } catch (error) {
    logger.warn('[ADV CLAIMS] Failed to load mode:', error);
    setAdvancedClaimsMode('standard');
    section.style.display = 'none';
    const receiptChkErr = $('requireReceiptNumberChk');
    if (receiptChkErr) receiptChkErr.checked = false;
  }
}

async function deleteResponsibleFromFirestore(docId) {
  const docRef = getAdvancedClaimsDocRef();
  if (!docRef || !docId) return;
  try {
    await docRef.collection('responsibles').doc(docId).delete();
  } catch (error) {
    logger.warn('[ADV CLAIMS] Failed to delete responsible:', error);
  }
}

function createResponsibleItem(docId, name) {
  const item = document.createElement('div');
  item.style.display = 'flex';
  item.style.alignItems = 'center';
  item.style.justifyContent = 'space-between';
  item.style.gap = '8px';
  item.style.padding = '10px 12px';
  item.style.marginBottom = '8px';
  item.style.border = '1px solid var(--border)';
  item.style.borderRadius = '10px';
  item.style.background = 'var(--surface)';

  const nameEl = document.createElement('span');
  nameEl.textContent = String(name || '').trim();
  nameEl.style.fontSize = '18px';
  nameEl.style.fontWeight = '700';
  nameEl.style.color = 'var(--text)';
  nameEl.style.flex = '1';
  nameEl.style.minWidth = '0';

  const deleteBtn = document.createElement('button');
  deleteBtn.type = 'button';
  deleteBtn.className = 'btn-ghost';
  deleteBtn.textContent = 'Șterge';
  deleteBtn.style.padding = '4px 8px';
  deleteBtn.style.fontSize = '11px';
  deleteBtn.style.lineHeight = '1.1';
  deleteBtn.style.fontWeight = '500';
  deleteBtn.style.minWidth = 'auto';
  deleteBtn.style.flex = '0 0 auto';
  deleteBtn.style.width = 'auto';
  deleteBtn.style.display = 'inline-flex';
  deleteBtn.style.alignItems = 'center';
  deleteBtn.style.justifyContent = 'center';
  deleteBtn.style.minHeight = '28px';
  deleteBtn.style.height = '28px';
  deleteBtn.style.whiteSpace = 'nowrap';
  deleteBtn.addEventListener('click', () => {
    deleteResponsibleFromFirestore(docId);
  });

  item.appendChild(nameEl);
  item.appendChild(deleteBtn);
  return item;
}

function subscribeResponsiblesList() {
  const list = $('responsiblesList');
  if (!list) return;

  if (responsiblesUnsub) {
    try {
      responsiblesUnsub();
    } catch (error) {
      logger.warn('[ADV CLAIMS] Failed to cleanup previous responsibles listener:', error);
    }
    responsiblesUnsub = null;
  }

  list.replaceChildren();

  const docRef = getAdvancedClaimsDocRef();
  if (!docRef) return;

  responsiblesUnsub = docRef
    .collection('responsibles')
    .orderBy('createdAt', 'asc')
    .onSnapshot((snap) => {
      list.replaceChildren();
      snap.forEach((doc) => {
        const data = doc.data() || {};
        list.appendChild(createResponsibleItem(doc.id, data.name));
      });
    }, (error) => {
      logger.warn('[ADV CLAIMS] Failed to subscribe responsibles:', error);
    });
}

function initAdvancedClaimsModeToggle() {
  const standardBtn = $('claimsStandardBtn');
  const advancedBtn = $('claimsAdvancedBtn');
  if (!standardBtn || !advancedBtn) return;

  // Ensure section exists and is hidden on load.
  const section = document.getElementById('advancedResponsiblesSection');
  if (section) section.style.display = 'none';

  // Prevent duplicate listeners when Admin UI is reset multiple times.
  if (!standardBtn.dataset.boundAdvancedClaimsMode) {
    standardBtn.addEventListener('click', () => {
      console.log('[ADV CLAIMS] clicked standard');
      const section = document.getElementById('advancedResponsiblesSection');
      if (section) section.style.display = 'none';
      setAdvancedClaimsMode('standard');
      saveAdvancedClaimsMode('standard');
    });
    standardBtn.dataset.boundAdvancedClaimsMode = '1';
  }
  if (!advancedBtn.dataset.boundAdvancedClaimsMode) {
    advancedBtn.addEventListener('click', () => {
      console.log('[ADV CLAIMS] clicked advanced');
      const section = document.getElementById('advancedResponsiblesSection');
      if (section) section.style.display = 'block';
      setAdvancedClaimsMode('advanced');
      saveAdvancedClaimsMode('advanced');
    });
    advancedBtn.dataset.boundAdvancedClaimsMode = '1';
  }

  setAdvancedClaimsMode('standard');
  loadAdvancedClaimsMode();
}

async function addResponsibleLocal() {
  const input = $('responsibleNameInput');
  if (!input) return;

  const name = String(input.value || '').trim();
  if (!name) return;

  const docRef = getAdvancedClaimsDocRef();
  if (!docRef) return;

  try {
    await docRef.collection('responsibles').add({
      name,
      active: true,
      createdAt: getServerTimestampValue()
    });
  } catch (error) {
    logger.warn('[ADV CLAIMS] Failed to add responsible:', error);
    return;
  }

  input.value = '';
  input.focus();
}

function initRequireReceiptNumberToggle() {
  const chk = $('requireReceiptNumberChk');
  if (!chk || chk.dataset.boundRequireReceipt) return;
  chk.dataset.boundRequireReceipt = '1';
  chk.addEventListener('change', async () => {
    const docRef = getAdvancedClaimsDocRef();
    if (!docRef) return;
    try {
      await docRef.set(
        {
          requireReceiptNumber: !!chk.checked,
          updatedAt: getServerTimestampValue()
        },
        { merge: true }
      );
      const cur = getCurrentSettings() || {};
      setCurrentSettings({ ...cur, requireReceiptNumber: !!chk.checked });
      import('../services/claims.js?v=83.79')
        .then((m) => (m.refreshClaimReceiptFieldVisibility ? m.refreshClaimReceiptFieldVisibility() : null))
        .catch(() => {});
    } catch (error) {
      logger.warn('[ADV CLAIMS] Failed to save requireReceiptNumber:', error);
    }
  });
}

function initAdvancedResponsiblesLocalUI() {
  const addBtn = $('addResponsibleBtn');
  const input = $('responsibleNameInput');
  const list = $('responsiblesList');
  if (!addBtn || !input || !list) return;

  subscribeResponsiblesList();

  if (!addBtn.dataset.boundAddResponsible) {
    addBtn.addEventListener('click', addResponsibleLocal);
    addBtn.dataset.boundAddResponsible = '1';
  }

  if (!input.dataset.boundAddResponsibleEnter) {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        addResponsibleLocal();
      }
    });
    input.dataset.boundAddResponsibleEnter = '1';
  }
}

export function updateClientButtonsGuard() {
  const currentSettings = getCurrentSettings();
  // butoanele de Mod client rămân active (fără PIN)
  void currentSettings;
  const allow = true;
  const b1 = $('openClientBtn');
  if (b1) b1.disabled = !allow;
  const b2 = $('roleClientBtn');
  if (b2) b2.disabled = !allow;
}

export function updateTriesCounter() {
  // ✅ FIX 4: Always use getCurrentSettings() not cached DOM state
  const currentSettings = getCurrentSettings();
  const c = $('triesCounter');
  if (!c) return;
  
  // ✅ FIX 3: Ensure exact field names
  const tot = parseInt(currentSettings?.totalTries || 0);
  const rem = parseInt(currentSettings?.remainingTries || 0);
  
  // ✅ FIX 1: Log for debugging
  console.log('[UPDATE TRIES COUNTER]', {
    totalTries: tot,
    remainingTries: rem,
    source: 'getCurrentSettings()'
  });
  
  const campaignActive = (currentSettings?.campaignStatus || 'draft') === 'active';
  if (!campaignActive) {
    c.textContent = '';
    return;
  }
  // Vizual ascuns (#triesCounter { display: none }) — doar text pentru debug/a11y
  c.textContent = tot > 0 ? `Încercări: ${Math.max(0, rem)}/${tot}` : '';
}

export function resetAdminUI() {
  try {
    const currentSettings = getCurrentSettings();
    
    // Removed 'op_company', 'op_cui', 'op_email' - these are now in Account page
    ['totalTries', 'buttonText', 'claimMessage', 'adminPass', 'campStart', 'campEnd'].forEach(id => {
      const el = $(id);
      if (!el) return;
      el.value = '';
    });
    
    const cont = $('prizeContainer');
    if (cont) cont.innerHTML = '';
    
    const pt = $('playsTotal');
    if (pt) pt.textContent = '0';

    const defMsgEl = $('defaultPrizeMsg');
    if (defMsgEl) {
      defMsgEl.value = (currentSettings?.defaultPrizeMsg) || 'Ne pare rău, nu ai câștigat!';
    }

    const defPrev = $('defaultPrizeImgPreview');
    if (defPrev) {
      // XSS-safe rendering: Use DOM API instead of innerHTML
      defPrev.replaceChildren();
      if (currentSettings?.defaultPrizeImg) {
        const safeUrl = sanitizeImageSrc(currentSettings.defaultPrizeImg);
        if (safeUrl) {
          const img = document.createElement('img');
          img.src = safeUrl;
          img.style.width = '120px';
          img.style.borderRadius = '10px';
          img.style.border = '1px solid #eee';
          defPrev.appendChild(img);
        }
      }
    }

    // Previews pentru background: settings.js gestionează starea locală nesalvată + remove
    if (typeof window !== 'undefined' && typeof window.renderUnsavedBgPreview === 'function') {
      window.renderUnsavedBgPreview();
    } else {
      clearPreview('bgPreview');
      const bgs = currentSettings?.bgURLs || (currentSettings?.bgURL ? [currentSettings.bgURL] : []);
      bgs.forEach(u => appendPreview('bgPreview', u));
    }

    renderPreview('confirmBgPreview', currentSettings?.confirmBgURL || null);

    // Slide duration
    const dSel = $('slideDuration');
    if (dSel) dSel.value = currentSettings?.slideDuration || 5;

    import('./menu-module-admin.js?v=83.79')
      .then((m) => m.hydrateMenuModuleAdminFromSettings(currentSettings))
      .catch(() => {});
    const communityEnabledEl = $('communityEnabled');
    if (communityEnabledEl) {
      communityEnabledEl.checked = currentSettings?.communityEnabled === true;
    }
    const communityMessageEl = $('communityMessage');
    if (communityMessageEl) {
      communityMessageEl.value = String(currentSettings?.communityMessage || '').trim().slice(0, 220);
    }

    // ✅ render recompense
    renderPrizeOptions();
    updateClientButtonsGuard();
    updateTriesCounter();
    initAdvancedClaimsModeToggle();
    initRequireReceiptNumberToggle();
    initAdvancedResponsiblesLocalUI();
    initClientLayoutPresetControls();
    
    // Initialize collapsible sections and update summaries
    setTimeout(() => {
      initAdminSections();
      updateSectionSummaries();
    }, 100);
  } catch (error) {
    logger.error('[admin] Error in resetAdminUI:', error);
  }
}

// Export function to update summaries (called after settings load)
export function refreshAdminSummaries() {
  updateSectionSummaries();
}

/** Salvează starea curentă revendicări avansate (mod + bon). */
export async function persistAdvancedClaimsModuleSettings() {
  const section = $('advancedResponsiblesSection');
  const isAdvanced = section && !section.classList.contains('hidden');
  const mode = isAdvanced ? 'advanced' : 'standard';
  await saveAdvancedClaimsMode(mode);

  const chk = $('requireReceiptNumberChk');
  const docRef = getAdvancedClaimsDocRef();
  if (chk && docRef) {
    await docRef.set(
      {
        requireReceiptNumber: !!chk.checked,
        updatedAt: getServerTimestampValue()
      },
      { merge: true }
    );
    setCurrentSettings({
      ...(getCurrentSettings() || {}),
      requireReceiptNumber: !!chk.checked
    });
    import('../services/claims.js?v=83.79')
      .then((m) => (m.refreshClaimReceiptFieldVisibility ? m.refreshClaimReceiptFieldVisibility() : null))
      .catch(() => {});
  }
}
